//
// Return an Image or Video player from Airtable field
//
import React from 'react';
import {format, parse} from 'date-fns';

import ChevronMotif from '../components/ChevronMotif';

/**
 * Item type for sticker rendering
 * Represents an Airtable item with id and other properties
 */
interface StickerItem {
  id: string;
  [key: string]: unknown;
}

// Methods extracted "as-is" from the original checkout screen
// Currency formatting: normalize to $ for consistency across platforms
// (mobile polyfill outputs A$, browser Intl outputs $ — strip the A prefix)
export const toAUD = (cents?: number, allowZero?: boolean) => {
  if (!cents) {
    return allowZero ? '$0.00' : '$--';
  }
  const formatted = (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
  });
  return formatted.replace('A$', '$');
};

export const toAUDAbs = (cents: number, allowZero: boolean) => {
  if (!cents) {
    return allowZero ? '$0.00' : '$--';
  }
  const formatted = Math.abs(cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
  });
  return formatted.replace('A$', '$');
};

export const renderSticker = (item: StickerItem, isProductScreen?: boolean) => (
  <ChevronMotif key={item.id} item={item} isProductScreen={isProductScreen} />
);

export const renderStickerSmall = (item: StickerItem) => (
  <ChevronMotif key={item.id} item={item} small />
);

export const formatDate = (dateString: string, inputFormat = 'yyyy-MM-dd') => {
  const parsed = parse(dateString, inputFormat, new Date());
  return format(parsed, 'dd/MM/yyyy');
};

/**
 * Split array into N chunks of approximately equal size
 */
export const chunkIntoN = <T,>(arr: T[], n: number): T[][] => {
  const size = Math.ceil(arr.length / n);
  return Array.from({length: n}, (_, i) => arr.slice(i * size, i * size + size));
};

// https://stackoverflow.com/a/8495740/1084192
export const arrayChunks = <T,>(array: T[], chunkSize: number) => {
  const chunks: T[][] = [];
  let chunkIndex = 0;
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    chunks[chunkIndex++] = chunk;
  }
  return chunks;
};

export const sum = (a: number, b: number) => a + b;

export const centsToDollarsString = (cents: number) => (cents / 100).toFixed(2);
